import { useEffect, useMemo } from 'react'
import { Routes as RoutesContainer, Route, Navigate } from 'react-router-dom'
import { ArcShellLayout } from 'components/ArcShell'
import { ProtectedRoute } from 'components/ProtectedRoute'
import { ErrorPermission } from 'pages/ErrorPermission'
import { SignIn } from 'pages/SignIn'
import { EditReport } from 'pages/Projects/Lessons'
import { NewProjectList } from 'pages/ProjectsList'
import { NewProject } from 'pages/NewProject'
import { ArcNewProject } from 'pages/ArcNewProject'
import { NewProjectView } from 'pages/NewProjectView'
import { MonitoringSmokeCheck } from 'pages/MonitoringSmokeCheck'
import { getProjectsScreenErrorBoundary } from 'helpers/bugsnag'
import { SignOut } from 'pages/SignOut'
import { useFeatureToggle, useFeatureToggleLoading } from 'hooks/useFeatureToggle'
import { useArcManagerDashboardEnabled } from 'hooks/useArcManagerDashboardEnabled'
import { FinalAssessmentPage } from 'pages/FinalAssessmentPage'
import { ReportsDashboard, ReportsStudent, ReportsGroup, GeneralReport } from 'pages/Reports'
import AnaliticalReport from 'pages/Reports/AnaliticalReport'
import { StudentCreationScreen } from 'pages/StudentCreationScreen'
import { GuardianDashboardPage } from 'pages/GuardianDashboard'
import { TeacherDashboardPage } from 'pages/TeacherDashboard'
import { StudentDashboardPage } from 'pages/StudentDashboard'
import { ForgotPasswordScreen } from 'pages/ForgotPassword'
import { ChangePasswordScreen } from 'pages/ForgotPassword/ChangePasswordScreen'
import Students from 'pages/Students'
import { ManageStudentsPage } from 'pages/ManageStudents'
import { StudentActivityPage } from 'pages/StudentActivity/StudentActivityPage'
import { ManageFacultyPage } from 'pages/ManageFaculty'
import { SchoolSettingsPage } from 'pages/SchoolSettings'
import { SchoolDetailsPage } from 'pages/SchoolDetails'
import { AdminConnectionsPage } from 'pages/AdminConnections'
import { SchoolIntegrationsPage } from 'pages/SchoolIntegrations'
import { FeedPage } from 'pages/Feed'
import { NotFoundPage } from 'pages/NotFound'
import { FinalProjectReportPage } from 'pages/FinalProjectReport'
import { TeacherSignup } from 'pages/TeacherSignup'
import { TeacherInviteSignup } from 'pages/TeacherSignup/TeacherInviteSignup'
import { StudentSignup } from 'pages/StudentSignup'
import { ArcProjectsPage } from 'pages/ArcProjectsList'
import { ArcDiskProjectRouteGate, ArcDiskStudentPage, useArcDiskDemoEnabled, useArcDiskDemoLoading } from 'arcdisk'
import { MyProfilePage } from 'pages/MyProfile'
import { TaskTrackerPage } from 'pages/TaskTracker'
import { StudentTasksPage } from 'pages/StudentTasks'
import { StudentTaskTrackerPage } from 'pages/StudentTaskTracker'
import { DevelopmentReportPage } from 'pages/DevelopmentReport'
import { ManagerDashboardPage } from 'pages/ManagerDashboard'
import { ResourceCenterPage, ResourceArticlePage } from 'pages/ResourceCenter'
import { EmailConfirmationPage } from 'pages/EmailConfirmation'
import { CleverCallback } from 'pages/CleverCallback'
import FeatureFlags from 'pages/FeatureFlags'

export type IProfiles = 'student' | 'tutor' | 'guardian' | 'manager'
export type USerKind = IProfiles[]

// The legacy manager dashboard lives in Rails, outside the SPA, so leaving
// requires a full document load — react-router's Navigate can't reach it.
// Rails sends managers of Arc schools back to /app/manager/dashboard, keeping
// both sides consistent on the same per-school flag.
const LegacyManagerDashboardRedirect = () => {
  useEffect(() => {
    window.location.replace('/manager/dashboard')
  }, [])
  return null
}

// Replaces the white screen when the projects screen crashes during render:
// the crash is already reported to the monitor by the boundary itself.
const ProjectsScreenCrashFallback = () => (
  <div style={{ fontFamily: 'sans-serif', padding: '3rem', textAlign: 'center' }}>
    <h2>Something went wrong on this screen.</h2>
    <p>The error was reported automatically. Reload the page to continue.</p>
    <button type='button' onClick={() => window.location.reload()}>
      Reload
    </button>
  </div>
)

export const Routes = () => {
  const isArcNewProjectEnabled = useFeatureToggle('arc-mosaico-project')
  const isManagerDashboardEnabled = useArcManagerDashboardEnabled()
  const isFeaturesLoading = useFeatureToggleLoading()
  const isArcDiskDemoEnabled = useArcDiskDemoEnabled()
  const isArcDiskDemoLoading = useArcDiskDemoLoading()
  // Created after startProjectsScreenMonitoring ran at app boot; memoized so
  // the boundary keeps a stable identity across renders
  const ProjectsErrorBoundary = useMemo(() => getProjectsScreenErrorBoundary(), [])

  return (
    <RoutesContainer>
      {/* Auth/full-screen routes: never wrapped by the persistent Arc shell */}
      <Route path='account/signin' element={<SignIn />} />
      <Route path='account/signout' element={<SignOut />} />
      <Route path='account/confirm' element={<EmailConfirmationPage />} />
      {/* Must match CLEVER_CALLBACK_PATH (services/cleverSso) and the URL registered in Clever */}
      <Route path='account/clever/callback' element={<CleverCallback />} />
      <Route path='account/teacher-signup' element={<TeacherSignup />} />
      <Route path='account/add-student' element={<StudentSignup />} />
      <Route path='hello-teacher/:token' element={<TeacherInviteSignup />} />
      <Route path='/forgot-password' element={<ForgotPasswordScreen />} />
      <Route path='/new-password' element={<ChangePasswordScreen />} />
      {/* App routes: pathless layout route mounts the Arc chrome once so it
          survives navigation (no-op pass-through when the flag is off) */}
      <Route element={<ArcShellLayout />}>
        <Route path='/error_permission' element={<ErrorPermission />} />
        <Route
          path='/feed'
          element={
            <ProtectedRoute enabledProfiles={['student', 'tutor', 'guardian', 'manager']}>
              <FeedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/arc_projects'
          element={
            isFeaturesLoading ? null : (
              isArcNewProjectEnabled ? (
                <ProtectedRoute enabledProfiles={['student', 'tutor', 'guardian', 'manager']}>
                  <ArcProjectsPage />
                </ProtectedRoute>
              ) : (
                <Navigate to="/projects" replace />
              )
            )
          }
        />
        <Route
          path='projects'
          element={
            isFeaturesLoading ? null : (
              isArcNewProjectEnabled ? (
                <Navigate to="/arc_projects?tab=my" replace />
              ) : (
                <ProtectedRoute enabledProfiles={['student', 'tutor', 'guardian', 'manager']}>
                  <NewProjectList />
                </ProtectedRoute>
              )
            )
          }
        />
        <Route
          path='/projects/new'
          element={
            isFeaturesLoading ? null : (
              <ProtectedRoute
                enabledProfiles={isArcNewProjectEnabled ? ['tutor', 'manager'] : ['tutor']}
                extraProtection={isArcNewProjectEnabled ? undefined : 'new-project'}
              >
                {isArcNewProjectEnabled ? <ArcNewProject /> : <NewProject />}
              </ProtectedRoute>
            )
          }
        />
        <Route
          path='/projects/:id/edit'
          element={
            isFeaturesLoading ? null : (
              <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
                {isArcNewProjectEnabled ? <ArcNewProject /> : <NewProject />}
              </ProtectedRoute>
            )
          }
        />
        {/* Static segment outranks :id in v6 matching; the smoke check must sit
            under /projects/ so it exercises the monitor's real path filter */}
        <Route path='/projects/smoke-check' element={<MonitoringSmokeCheck />} />
        <Route
          path='/projects/:id'
          element={
            <ProtectedRoute enabledProfiles={['student', 'tutor', 'guardian', 'manager']}>
              {/* ArcDisk demo: the gate renders the demo project page for the
                  three demo card ids (demo mode only), Mosaico's view otherwise */}
              <ArcDiskProjectRouteGate>
                {ProjectsErrorBoundary ? (
                  <ProjectsErrorBoundary FallbackComponent={ProjectsScreenCrashFallback}>
                    <NewProjectView />
                  </ProjectsErrorBoundary>
                ) : (
                  <NewProjectView />
                )}
              </ArcDiskProjectRouteGate>
            </ProtectedRoute>
          }
        />
        {(isArcDiskDemoLoading || isArcDiskDemoEnabled) && (
          <Route
            path='/projects/:id/students/:studentId'
            element={
              <ProtectedRoute enabledProfiles={['student', 'tutor', 'guardian', 'manager']}>
                <ArcDiskStudentPage />
              </ProtectedRoute>
            }
          />
        )}
        <Route
          path='projects/lessons/:lessonId/edit_report'
          element={
            <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
              <EditReport />
            </ProtectedRoute>
          }
        />
        <Route
          path='/projects/final_assessments/:lessonId'
          element={
            <ProtectedRoute enabledProfiles={['student', 'tutor', 'manager']}>
              <FinalAssessmentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/final_project_report/:id'
          element={
            <ProtectedRoute enabledProfiles={['student', 'guardian', 'tutor', 'manager']}>
              <FinalProjectReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/reports/dashboard'
          element={
            <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
              <ReportsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path='/reports/student'
          element={
            <ProtectedRoute enabledProfiles={['student', 'tutor', 'guardian', 'manager']}>
              <ReportsStudent />
            </ProtectedRoute>
          }
        />
        <Route
          path='/reports/general'
          element={
            <ProtectedRoute enabledProfiles={['student', 'tutor', 'guardian', 'manager']}>
              <GeneralReport />
            </ProtectedRoute>
          }
        />
        <Route
          path='/reports/group'
          element={
            <ProtectedRoute enabledProfiles={['student', 'tutor', 'guardian', 'manager']}>
              <ReportsGroup />
            </ProtectedRoute>
          }
        />
        <Route
          path='/teacher/manage-students'
          element={
            <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
              <ManageStudentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/teacher/manage-students/:studentId/activity'
          element={
            <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
              <StudentActivityPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/teacher/manage-faculty'
          element={
            <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
              <ManageFacultyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/teacher/settings'
          element={
            <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
              <SchoolSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/teacher/school-details'
          element={
            <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
              <SchoolDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/teacher/google-classroom'
          element={
            <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
              <SchoolIntegrationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/admin/connections'
          element={
            // Profiles stay broad here: the page itself blocks anyone
            // without the super_admin flag (AdminConnectionsGate).
            <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
              <AdminConnectionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/learners'
          element={
            <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
              <Students />
            </ProtectedRoute>
          }
        />
        <Route
          path='/learners/new'
          element={
            <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
              <StudentCreationScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path='/learners/new/:id'
          element={
            <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
              <StudentCreationScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path='/reports/student'
          element={
            <ProtectedRoute enabledProfiles={['student', 'tutor', 'guardian', 'manager']}>
              <ReportsStudent />
            </ProtectedRoute>
          }
        />

        <Route
          path='/guardian_room/dashboard'
          element={
            <ProtectedRoute enabledProfiles={['guardian']}>
              <GuardianDashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path='/teacher/dashboard'
          element={
            <ProtectedRoute enabledProfiles={['tutor']}>
              <TeacherDashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path='/student_room/dashboard'
          element={
            <ProtectedRoute enabledProfiles={['student']}>
              <StudentDashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path='/reports/dashboard'
          element={
            <ProtectedRoute enabledProfiles={['student', 'tutor', 'guardian', 'manager']}>
              <ReportsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path='/reports/group'
          element={
            <ProtectedRoute enabledProfiles={['guardian', 'manager']}>
              <ReportsGroup />
            </ProtectedRoute>
          }
        />
        <Route
          path='/reports/group/analitical/:groupId'
          element={
            <ProtectedRoute enabledProfiles={['guardian', 'manager']}>
              <AnaliticalReport />
            </ProtectedRoute>
          }
        />

        <Route
          path='/admin/feature-flags'
          element={
            <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
              <FeatureFlags />
            </ProtectedRoute>
          }
        />

        <Route
          path='/app/profile'
          element={
            isFeaturesLoading ? null : (
              isArcNewProjectEnabled ? (
                <ProtectedRoute enabledProfiles={['student', 'tutor', 'guardian', 'manager']}>
                  <MyProfilePage />
                </ProtectedRoute>
              ) : (
                <Navigate to='/' replace />
              )
            )
          }
        />

        <Route
          path='/app/task-tracker'
          element={
            isFeaturesLoading ? null : (
              isArcNewProjectEnabled ? (
                <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
                  <TaskTrackerPage />
                </ProtectedRoute>
              ) : (
                <Navigate to='/' replace />
              )
            )
          }
        />

        <Route
          path='/app/student-tasks'
          element={
            isFeaturesLoading ? null : (
              isArcNewProjectEnabled ? (
                <ProtectedRoute enabledProfiles={['student']}>
                  <StudentTasksPage />
                </ProtectedRoute>
              ) : (
                <Navigate to='/' replace />
              )
            )
          }
        />

        <Route
          path='/app/student-task-tracker'
          element={
            isFeaturesLoading ? null : (
              isArcNewProjectEnabled ? (
                <ProtectedRoute enabledProfiles={['student']}>
                  <StudentTaskTrackerPage />
                </ProtectedRoute>
              ) : (
                <Navigate to='/' replace />
              )
            )
          }
        />

        <Route
          path='/app/development-report'
          element={
            isFeaturesLoading ? null : (
              isArcNewProjectEnabled ? (
                <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
                  <DevelopmentReportPage />
                </ProtectedRoute>
              ) : (
                <Navigate to='/' replace />
              )
            )
          }
        />

        <Route
          path='/app/manager/dashboard'
          element={
            isFeaturesLoading ? null : (
              isManagerDashboardEnabled ? (
                <ProtectedRoute enabledProfiles={['manager']}>
                  <ManagerDashboardPage />
                </ProtectedRoute>
              ) : (
                <LegacyManagerDashboardRedirect />
              )
            )
          }
        />

        <Route
          path='/app/resource-center'
          element={
            isFeaturesLoading ? null : (
              isArcNewProjectEnabled ? (
                <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
                  <ResourceCenterPage />
                </ProtectedRoute>
              ) : (
                <Navigate to='/' replace />
              )
            )
          }
        />

        <Route
          path='/app/resource-center/:articleSlug'
          element={
            isFeaturesLoading ? null : (
              isArcNewProjectEnabled ? (
                <ProtectedRoute enabledProfiles={['tutor', 'manager']}>
                  <ResourceArticlePage />
                </ProtectedRoute>
              ) : (
                <Navigate to='/' replace />
              )
            )
          }
        />

      </Route>

      <Route path='*' element={<NotFoundPage />} />
    </RoutesContainer>
  )
}
